import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from 'flowbite-react';
import { HistoryIcon, InfoIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { RouteIndex } from '../../../app/routeIndex';
import { useLocalStorage } from '../../../hooks/useLocalStorage.hook';
import { pathWithParams } from '../../../lib/routing.utils';

export const RevisionDisclaimer: React.FC<{
    documentId: string;
    revisionId: string;
}> = ({
    documentId,
    revisionId
}) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const nav = useNavigate();
    const [ isHidden, setIsHidden ] = useLocalStorage('hide-revision-disclaimer', false);

    const hideDisclaimer = () => {
        setIsHidden(true);
    };

    const setAsMain = useMutation({
        mutationFn: async () => {
            await api.v1.documents.setRevisionAsMain(documentId, revisionId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['document', documentId] });
            queryClient.invalidateQueries({ queryKey: ['document', documentId, 'revisions'] });
        }
    });

    const goBackToMainRevision = useCallback(() => {
        nav(pathWithParams(
            RouteIndex.documentDetail.edit,
            { documentId }
        ));
    }, [ documentId ]);

    return (
        <>
            {!isHidden && (
                <div className='rounded-xl bg-gray-200 p-4 md:p-6 text-sm mb-4'>
                    <p>Upravujete revizi vytvořenou pomocí AI korektury. Projděte si jednotlivé části dokumentu a můžete přijmou či odmítnout navržené změny. Zároveň můžete dokument jakkoliv editovat. Až budete se změnami spokojeni, <strong>nastavte tuto revizi jako hlavní.</strong></p>
                    <p className='mt-4'>K této revizi se můžete kdykoliv vrátit pomocí tlačítka revizí <HistoryIcon className='inline-block w-4 h-4 text-gray-600' />.</p>
                    <p className='mt-4 text-right'>
                        <button
                            className='p-0 underline hover:no-underline text-gray-700'
                            onClick={hideDisclaimer}
                        >Již nezobrazovat</button>
                    </p>
                </div>
            )}
            <div className='flex flex-col gap-2 rounded-xl p-3 self-stretch border border-gray-300'>
                <div className='flex flex-row items-center gap-2'>
                    <InfoIcon className='w-4 h-4 flex-shrink-0 text-gray-500' />
                    <p className='grow text-xs font-semibold'>Upravujete revizi vytvořenou pomocí AI korektury.</p>
                </div>
                <Button
                    onClick={() => setAsMain.mutate()}
                    disabled={setAsMain.isPending}
                    color='primary'
                    size='xs'
                    className='w-full'
                >Nastavit jako hlavní revizi</Button>
                <Button
                    onClick={goBackToMainRevision}
                    color='gray'
                    size='xs'
                    className='w-full'
                >Zpět na hlavní revizi</Button>
            </div>
        </>
    );
};
